/**
 * WorkdayRing.js
 *
 * Circular progress ring (SVG) toward the daily workday target. Orange brand while on track, amber past the target,
 * red one hour later (see {@link module:helpers/workdayRules.progressModel}). Optional centred children.
 *
 * @module components/workday/WorkdayRing
 */

import React from 'react';
import {StyleSheet, View} from 'react-native';
import Svg, {Circle} from 'react-native-svg';
import {BRAND} from '../../helpers/managementTheme';

/** Ring colour per tone. */
export const RING_TONES = Object.freeze({ok: BRAND.base, warn: '#F59E0B', danger: '#DC2626'});

/**
 * @param {object} props props.
 * @param {number} [props.size] outer diameter.
 * @param {number} [props.stroke] stroke width.
 * @param {number} props.progress 0..1.
 * @param {'ok'|'warn'|'danger'} [props.tone] colour tone.
 * @param {string} [props.trackColor] track colour.
 * @param {React.ReactNode} [props.children] centre content.
 * @returns {JSX.Element} ring.
 */
export default function WorkdayRing({size = 120, stroke = 10, progress = 0, tone = 'ok', trackColor = 'rgba(148,163,184,0.25)', children}) {
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const clamped = Math.max(0, Math.min(1, Number(progress) || 0));

    return (
        <View style={{width: size, height: size}}>
            <Svg width={size} height={size} style={styles.svg}>
                <Circle cx={size / 2} cy={size / 2} r={radius} stroke={trackColor} strokeWidth={stroke} fill="none" />
                {clamped > 0 && (
                    <Circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke={RING_TONES[tone] || RING_TONES.ok}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        fill="none"
                        strokeDasharray={`${circumference} ${circumference}`}
                        strokeDashoffset={circumference * (1 - clamped)}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                )}
            </Svg>
            {!!children && <View style={styles.center}>{children}</View>}
        </View>
    );
}

const styles = StyleSheet.create({
    svg: {position: 'absolute'},
    center: {...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center'},
});
